import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, Send, Loader2, RefreshCw, ChevronDown, ChevronRight, CheckCircle2, XCircle, Clock } from 'lucide-react';
import {
  XbPage, WithScheme, SchemeHeading, Card, Fact, C, Notice, useSchemeDetail, errorText,
} from './common';
import xbApi from '../../services/xbApi';
import { SCENARIOS, CASE_STATES, caseOpen, schemeBase } from '../../utils/crossBorder';
import { formatDate } from '../../utils/helpers';

const TONE = {
  ok: 'bg-green-50 text-green-700 border-green-200',
  danger: 'bg-red-50 text-red-700 border-red-200',
  warn: 'bg-amber-50 text-amber-800 border-amber-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
};

/**
 * The receiving side of a scheme: the Sandbox pays the participant from a
 * synthetic institution on the far side of a corridor, for a scenario, and
 * judges its answer against what the scheme expects.
 */
export default function XbIncoming() {
  return (
    <XbPage icon={Inbox} title="Incoming payments" wide
      intro="Have the Sandbox pay you, as a bank on the other side of a corridor would, and see whether your answer is the one the scheme expects.">
      <WithScheme>{(scheme) => <IncomingBody scheme={scheme} />}</WithScheme>
    </XbPage>
  );
}

function IncomingBody({ scheme }) {
  const s = scheme.profile;
  const polls = s.offers?.polls;
  const { detail, error: detailError } = useSchemeDetail(scheme);
  const [cases, setCases] = useState(null);
  const [listError, setListError] = useState(null);

  const load = useCallback(() => {
    xbApi.incoming(scheme.profileId)
      .then((list) => { setCases(Array.isArray(list) ? list : []); setListError(null); })
      .catch((e) => setListError(errorText(e)));
  }, [scheme.profileId]);

  useEffect(() => { load(); }, [load]);

  // While a case waits on an answer, look again every few seconds: its deadline may pass.
  const anyOpen = (cases || []).some(caseOpen);
  useEffect(() => {
    if (!anyOpen) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [anyOpen, load]);

  return (
    <>
      <SchemeHeading summary={s} />

      <Notice tone="info" title="How the payment reaches you">
        {polls ? (
          <>It waits in your inbox, from the scheme ({s.wire.hubMemberId}) to your member id ({scheme.memberId}). Poll
            {' '}<C>GET {schemeBase(s.id)}/papss/Message</C>, then answer with a signed pacs.002 for the group
            {' '}(<C>{s.replies.incomingReply}</C> or <C>RJCT</C> with a reason) within {s.replies.responseSeconds} s,
            {' '}to <C>POST {schemeBase(s.id)}/papss/Message</C>. Your answer is the payment's receipt: do not confirm
            it with MessageAck. You can also poll and answer from <Link to="/xb/samples" className="underline">Message
            Samples</Link>.</>
        ) : (
          <>It is delivered to your <Link to="/iso20022/callback-endpoints" className="underline">incoming-credit
            callback endpoint</Link>, switch-signed. Answer with a signed pacs.002 in the response, or post it to
            {' '}<C>POST {schemeBase(s.id)}/incoming/status</C> within {s.replies.responseSeconds} s
            {s.replies.holdMaxSeconds > 0 ? `; a payment you hold (PDNG) must be decided within ${s.replies.holdMaxSeconds} s` : ''}.</>
        )}
      </Notice>

      {detailError && <Notice tone="danger" title="The scenarios could not be read">{detailError}</Notice>}
      {detail && <TriggerCard scheme={scheme} offers={detail.incoming} onSent={load} />}

      <Card title="Your incoming payments" icon={Inbox}
        actions={(
          <button type="button" onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" aria-hidden="true" /> Refresh
          </button>
        )}>
        {listError && <p className="text-sm text-red-600 mb-2">{listError}</p>}
        {cases === null && !listError && (
          <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Loading…</p>
        )}
        {cases && cases.length === 0 && <p className="text-sm text-gray-500">None yet. Send yourself one above.</p>}
        {cases && cases.length > 0 && (
          <div className="divide-y divide-gray-100">
            {cases.map((c) => <CaseRow key={c.id} c={c} />)}
          </div>
        )}
      </Card>
    </>
  );
}

function TriggerCard({ scheme, offers, onSent }) {
  const s = scheme.profile;
  const first = (offers || []).find((o) => o.offered);
  const [scenario, setScenario] = useState(first?.scenario || 'ACCEPT');
  const [account, setAccount] = useState('0788123456');
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);   // { ok, text }

  const chosen = (offers || []).find((o) => o.scenario === scenario);
  const into = (s.corridors || []).find((c) => c.to === scheme.homeCountry && c.receive === scheme.homeCurrency);

  const send = async () => {
    setSending(true); setResult(null);
    try {
      const out = await xbApi.triggerIncoming(scheme.profileId, {
        scenario, creditorAccount: account.trim() || null, creditorName: name.trim() || null,
        amount: amount.trim() || null,
      });
      setResult({ ok: true, text: `Case ${out.incoming.id}: ${out.note}` });
      onSent();
    } catch (e) {
      const code = e?.response?.data?.error;
      setResult({ ok: false, text: errorText(e), endpoint: code === 'no_incoming_endpoint' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card title="Send me a payment" icon={Send}>
      {!into && (
        <p className="text-sm text-amber-800 mb-3">This scheme carries no payments into {scheme.homeCountry} in
          {' '}{scheme.homeCurrency}, so none can be sent to you.</p>
      )}
      <fieldset className="space-y-2" disabled={!into}>
        <legend className="text-sm font-medium text-gray-700 mb-1">Scenario</legend>
        {(offers || []).map((o) => (
          <label key={o.scenario}
            className={`flex items-start gap-3 p-2.5 rounded-lg border ${scenario === o.scenario ? 'border-primary-300 bg-primary-50'
              : 'border-gray-200'} ${o.offered ? 'cursor-pointer' : 'opacity-60'}`}>
            <input type="radio" name="scenario" value={o.scenario} checked={scenario === o.scenario}
              disabled={!o.offered} onChange={() => setScenario(o.scenario)} className="mt-1" />
            <span className="text-sm">
              <span className="font-medium text-gray-900">{SCENARIOS[o.scenario] || o.scenario}</span>
              {' '}<span className="text-gray-500">expects</span> <C>{o.expects}</C>
              <span className="block text-gray-600 mt-0.5">
                {o.offered ? o.why : `Not offered: ${o.notOffered}.`}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <div className="grid gap-3 sm:grid-cols-3 mt-4">
        <label className="block text-xs font-medium text-gray-700">
          Creditor account
          <input value={account} onChange={(e) => setAccount(e.target.value)} disabled={scenario === 'UNKNOWN_ACCOUNT'}
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-50" />
          {scenario === 'UNKNOWN_ACCOUNT' && <span className="block mt-1 text-gray-500 font-normal">The Sandbox picks an account no one holds.</span>}
        </label>
        <label className="block text-xs font-medium text-gray-700">
          Creditor name <span className="font-normal text-gray-500">(optional)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={`Test Beneficiary ${scheme.homeCountry}`}
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="block text-xs font-medium text-gray-700">
          Amount sent{into ? ` in ${into.send}` : ''} <span className="font-normal text-gray-500">(optional)</span>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="1000" inputMode="decimal"
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
        </label>
      </div>

      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <button type="button" onClick={send} disabled={sending || !into || !chosen?.offered}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Send className="w-4 h-4" aria-hidden="true" />}
          Send the payment
        </button>
        {into && <span className="text-xs text-gray-500">From a synthetic bank in {into.from}, paid out to you in {into.receive}
          {' '}at the teaching rate.</span>}
      </div>
      {result && (
        <p className={`text-sm mt-3 ${result.ok ? 'text-green-700' : 'text-red-600'}`}>
          {result.text}
          {result.endpoint && <> <Link to="/iso20022/callback-endpoints" className="underline">Set up the endpoint</Link>.</>}
        </p>
      )}
    </Card>
  );
}

function CaseRow({ c }) {
  const [open, setOpen] = useState(false);
  const st = CASE_STATES[c.state] || { label: c.state, tone: null };
  const Icon = c.state === 'PASS' ? CheckCircle2 : c.state === 'FAIL' ? XCircle : Clock;
  return (
    <div className="py-3">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="w-full flex items-start gap-3 text-left">
        {open ? <ChevronDown className="w-4 h-4 mt-1 text-gray-400 shrink-0" aria-hidden="true" />
              : <ChevronRight className="w-4 h-4 mt-1 text-gray-400 shrink-0" aria-hidden="true" />}
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900">#{c.id} {SCENARIOS[c.scenario] || c.scenario}</span>
            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${TONE[st.tone] || ''}`}>
              <Icon className="w-3.5 h-3.5" aria-hidden="true" /> {st.label}</span>
            {c.credited && <span className="text-xs text-green-700">credited</span>}
          </span>
          <span className="block text-sm text-gray-600 mt-0.5">
            {c.instructed} → {c.paid}; expects <C>{c.expected}</C>
            {c.firstAnswer ? <>; answered {c.firstAnswer}</> : null}
          </span>
          {c.verdict && <span className="block text-sm text-gray-800 mt-0.5">{c.verdict}</span>}
        </span>
        <span className="text-xs text-gray-500 shrink-0">{c.issuedAt ? formatDate(c.issuedAt, 'PPp') : ''}</span>
      </button>
      {open && (
        <dl className="mt-2 ml-7 grid gap-x-8 md:grid-cols-2">
          <Fact label="Why this is expected">{c.why}</Fact>
          <Fact label="Answer due by">{c.respondBy ? formatDate(c.respondBy, 'PPpp') : '—'}</Fact>
          {c.holdBy && <Fact label="Hold decided by">{formatDate(c.holdBy, 'PPpp')}</Fact>}
          {c.lastAnswer && <Fact label="Final answer">{c.lastAnswer}</Fact>}
          <Fact label="Message id" mono>{c.msgId}</Fact>
          <Fact label="End-to-end id" mono>{c.endToEndId}</Fact>
          <Fact label="From">{c.debtor}</Fact>
          <Fact label="To account" mono>{c.creditorAccount}</Fact>
          <Fact label="Rate">{c.rate}</Fact>
          {c.valueAt && <Fact label="Value">{c.valueAt}</Fact>}
          <Fact label="Delivery">{c.delivered ? c.deliveryDetail || 'delivered' : c.deliveryDetail || 'not delivered'}</Fact>
          <Fact label="Answers taken at" mono>{c.answerAt}</Fact>
        </dl>
      )}
    </div>
  );
}
